/**
 * Tag API Contract
 *
 * Mirrors: backend/src/routes/tag.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateTagGroupBodySchema, UpdateTagGroupInputSchema, CreateTagInputSchema, UpdateTagInputSchema, CreateResourceTagInputSchema, TaggableResourceTypeSchema, TagGroupResponseSchema, TagResponseSchema, TagWithGroupResponseSchema, TagGroupWithTagsResponseSchema, ResourceTagResponseSchema, ResourceTagWithDetailsResponseSchema, } from "@hourly/api/tag";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// Tag Group Contracts
// ============================================
/**
 * POST /api/tag-groups
 * Create a new tag group
 */
export const createTagGroup = oc
    .route({
    method: "POST",
    path: "/tag-groups",
    summary: "Create a new tag group",
    tags: ["Tags"],
    successStatus: 201,
})
    .input(CreateTagGroupBodySchema.omit({ companyId: true }))
    .output(TagGroupResponseSchema);
/**
 * GET /api/tag-groups/:id
 * Get a tag group by ID
 */
export const getTagGroup = oc
    .route({
    method: "GET",
    path: "/tag-groups/{id}",
    summary: "Get tag group by ID",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }))
    .output(TagGroupResponseSchema);
/**
 * GET /api/tag-groups/:id/with-tags
 * Get a tag group by ID with all tags
 */
export const getTagGroupWithTags = oc
    .route({
    method: "GET",
    path: "/tag-groups/{id}/with-tags",
    summary: "Get tag group by ID with all tags",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }))
    .output(TagGroupWithTagsResponseSchema);
/**
 * GET /api/tag-groups
 * Get all tag groups for the authenticated user's company
 */
export const getTagGroupsByCompany = oc
    .route({
    method: "GET",
    path: "/tag-groups",
    summary: "List tag groups for a company",
    tags: ["Tags"],
})
    .input(z.object({}))
    .output(z.array(TagGroupResponseSchema));
/**
 * PUT /api/tag-groups/:id
 * Update a tag group
 */
export const updateTagGroup = oc
    .route({
    method: "PUT",
    path: "/tag-groups/{id}",
    summary: "Update a tag group",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateTagGroupInputSchema))
    .output(TagGroupResponseSchema);
/**
 * DELETE /api/tag-groups/:id
 * Delete a tag group (cascades to tags and resource tags)
 */
export const deleteTagGroup = oc
    .route({
    method: "DELETE",
    path: "/tag-groups/{id}",
    summary: "Delete a tag group",
    tags: ["Tags"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
// ============================================
// Tag Contracts
// ============================================
/**
 * POST /api/tag-groups/:tagGroupId/tags
 * Create a new tag in a tag group
 */
export const createTag = oc
    .route({
    method: "POST",
    path: "/tag-groups/{tagGroupId}/tags",
    summary: "Create a new tag in a tag group",
    tags: ["Tags"],
    successStatus: 201,
})
    .input(z.object({ tagGroupId: uuidSchema }).merge(CreateTagInputSchema))
    .output(TagResponseSchema);
/**
 * GET /api/tags/:id
 * Get a tag by ID
 */
export const getTag = oc
    .route({
    method: "GET",
    path: "/tags/{id}",
    summary: "Get tag by ID",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }))
    .output(TagResponseSchema);
/**
 * GET /api/tags/:id/with-group
 * Get a tag by ID with tag group
 */
export const getTagWithGroup = oc
    .route({
    method: "GET",
    path: "/tags/{id}/with-group",
    summary: "Get tag by ID with tag group",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }))
    .output(TagWithGroupResponseSchema);
/**
 * GET /api/tag-groups/:tagGroupId/tags
 * Get all tags in a tag group
 */
export const getTagsByTagGroup = oc
    .route({
    method: "GET",
    path: "/tag-groups/{tagGroupId}/tags",
    summary: "List tags in a tag group",
    tags: ["Tags"],
})
    .input(z.object({ tagGroupId: uuidSchema }))
    .output(z.array(TagResponseSchema));
/**
 * PUT /api/tags/:id
 * Update a tag
 */
export const updateTag = oc
    .route({
    method: "PUT",
    path: "/tags/{id}",
    summary: "Update a tag",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }).merge(UpdateTagInputSchema))
    .output(TagResponseSchema);
/**
 * DELETE /api/tags/:id
 * Delete a tag (cascades to resource tags)
 */
export const deleteTag = oc
    .route({
    method: "DELETE",
    path: "/tags/{id}",
    summary: "Delete a tag",
    tags: ["Tags"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
// ============================================
// Resource Tag Contracts
// ============================================
/**
 * POST /api/resource-tags
 * Apply a tag to a resource
 */
export const createResourceTag = oc
    .route({
    method: "POST",
    path: "/resource-tags",
    summary: "Apply a tag to a resource",
    tags: ["Tags"],
    successStatus: 201,
})
    .input(CreateResourceTagInputSchema)
    .output(ResourceTagResponseSchema);
/**
 * GET /api/resource-tags/:id
 * Get a resource tag by ID
 */
export const getResourceTag = oc
    .route({
    method: "GET",
    path: "/resource-tags/{id}",
    summary: "Get resource tag by ID",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }))
    .output(ResourceTagResponseSchema);
/**
 * GET /api/resource-tags/:id/with-details
 * Get a resource tag by ID with tag and tag group details
 */
export const getResourceTagWithDetails = oc
    .route({
    method: "GET",
    path: "/resource-tags/{id}/with-details",
    summary: "Get resource tag by ID with tag and tag group details",
    tags: ["Tags"],
})
    .input(z.object({ id: uuidSchema }))
    .output(ResourceTagWithDetailsResponseSchema);
/**
 * GET /api/resources/:resourceType/:resourceId/tags
 * Get all tags on a specific resource
 */
export const getResourceTagsByResource = oc
    .route({
    method: "GET",
    path: "/resources/{resourceType}/{resourceId}/tags",
    summary: "Get all tags on a specific resource",
    tags: ["Tags"],
})
    .input(z.object({
    resourceType: TaggableResourceTypeSchema,
    resourceId: uuidSchema,
}))
    .output(z.array(ResourceTagResponseSchema));
/**
 * GET /api/tags/:tagId/resources
 * Get all resources with a specific tag
 */
export const getResourcesByTag = oc
    .route({
    method: "GET",
    path: "/tags/{tagId}/resources",
    summary: "Get all resources with a specific tag",
    tags: ["Tags"],
})
    .input(z.object({ tagId: uuidSchema }))
    .output(z.array(ResourceTagResponseSchema));
/**
 * DELETE /api/resource-tags/:id
 * Remove a tag from a resource
 */
export const deleteResourceTag = oc
    .route({
    method: "DELETE",
    path: "/resource-tags/{id}",
    summary: "Remove a tag from a resource",
    tags: ["Tags"],
    successStatus: 204,
})
    .input(z.object({ id: uuidSchema }))
    .output(z.void());
// ============================================
// Contract Router
// ============================================
export const tagContract = {
    // Tag Group operations
    createTagGroup,
    getTagGroup,
    getTagGroupWithTags,
    getTagGroupsByCompany,
    updateTagGroup,
    deleteTagGroup,
    // Tag operations
    createTag,
    getTag,
    getTagWithGroup,
    getTagsByTagGroup,
    updateTag,
    deleteTag,
    // Resource Tag operations
    createResourceTag,
    getResourceTag,
    getResourceTagWithDetails,
    getResourceTagsByResource,
    getResourcesByTag,
    deleteResourceTag,
};
//# sourceMappingURL=tag.js.map